import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
  IconButton,
  Drawer,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material';
import { tokens } from '../theme';
import { useSettings } from '../contexts/SettingsContext';

const DraftboardControls = ({
  // Settings chips (moved out into SettingsChips; keep props for compatibility if needed)
  scoringType,
  platform,
  draftId,
  
  // Position filters
  positionFilters,
  handlePositionFilterChange,
  positionColors,
  
  // Sorting and live mode
  sortOption,
  handleSortChange,
  isLiveMode,
  isConnected,
  handleLiveModeChange,
  
  // Connect/disconnect
  connectLiveDraft,
  // Rendering context
  context = 'topbar', // 'topbar' | 'drawer'
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { resetDraftboard, isSuperflex } = useSettings();
  const [resetDrafted, setResetDrafted] = useState(true);
  const [resetKeepers, setResetKeepers] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isDrawerContext = context === 'drawer';

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap" sx={{ maxWidth: '100%' }}>
      {/* Settings chips moved to SettingsChips component in Topbar */}

      {/* Position Switches Group (hidden on md down, moved to dialog) */}
      <Box
        sx={{
          minWidth: "fit-content",
          border: `1px solid ${colors.primary[600]}`,
          borderRadius: "6px",
          padding: "8px 12px",
          backgroundColor: colors.primary[400],
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          minHeight: 44,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {Object.entries(positionFilters).map(([position, checked]) => (
            <Chip
              key={position}
              label={position}
              clickable
              onClick={() => handlePositionFilterChange(position)}
              variant={checked ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontWeight: checked ? 700 : 500,
                backgroundColor: checked ? (positionColors[position] || colors.blueAccent[600]) : 'transparent',
                color: checked ? colors.grey[900] : colors.grey[100],
                borderColor: colors.primary[600],
                '&:hover': {
                  backgroundColor: checked ? (positionColors[position] || colors.blueAccent[500]) : colors.primary[600],
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Sorting Options Group (hidden on md down, moved to dialog) */}
      <Box
        sx={{
          minWidth: "fit-content",
          border: `1px solid ${colors.primary[600]}`,
          borderRadius: "6px",
          padding: "8px 12px",
          backgroundColor: colors.primary[400],
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          minHeight: 44,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
          {/* Live toggle as Checkbox */}
          <Tooltip title={!isConnected ? "Connect to a live draft to enable live mode" : ""}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConnected && isLiveMode}
                  onChange={handleLiveModeChange}
                  disabled={!isConnected}
                  size="small"
                  sx={{
                    color: colors.grey[400],
                    p: '2px',
                    "&.Mui-checked": { color: colors.greenAccent[500] },
                    "&.Mui-disabled": { color: colors.grey[600] },
                    "& .MuiSvgIcon-root": { fontSize: '1.2rem' },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '11px', color: !isConnected ? colors.grey[500] : isLiveMode ? colors.greenAccent[400] : colors.grey[100], fontWeight: isLiveMode ? 'bold' : 'normal' }}>
                  Live
                </Typography>
              }
              sx={{ m: 0, "&.Mui-disabled": { cursor: 'not-allowed' } }}
            />
          </Tooltip>

          {/* Sort selector as Radio buttons */}
          <RadioGroup
            row
            value={sortOption}
            onChange={(e) => handleSortChange(e)}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormControlLabel
              value="adp"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: colors.grey[300],
                    "&.Mui-checked": { color: colors.greenAccent[500] },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '11px', fontWeight: sortOption === 'adp' ? 700 : 500, color: sortOption === 'adp' ? colors.greenAccent[400] : colors.grey[100] }}>
                  ADP
                </Typography>
              }
              sx={{ m: 0, mr: 1 }}
            />
            <FormControlLabel
              value="ecr"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: colors.grey[300],
                    "&.Mui-checked": { color: colors.blueAccent[500] },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '11px', fontWeight: sortOption === 'ecr' ? 700 : 500, color: sortOption === 'ecr' ? colors.blueAccent[400] : colors.grey[100] }}>
                  ECR
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </RadioGroup>
        </Box>
      </Box>

      {/* Connect/Disconnect Draft Button (hidden on md down, compact on lg) */}
      <Box sx={{ minWidth: "fit-content", display: isDrawerContext ? 'none' : { xs: 'none', md: 'block' } }}>
        <Button
          variant={isConnected ? "outlined" : "contained"}
          onClick={connectLiveDraft}
          sx={{
            backgroundColor: isConnected ? "transparent" : colors.blueAccent[700],
            borderColor: isConnected ? colors.redAccent[500] : "transparent",
            color: isConnected ? colors.redAccent[500] : colors.grey[100],
            fontSize: { md: "12px", lg: "12px", xl: "14px" },
            fontWeight: "bold",
            padding: { md: "6px 10px", lg: "6px 12px", xl: "8px 16px" },
            minWidth: { md: 0, lg: 0, xl: 64 },
            "&:hover": {
              backgroundColor: isConnected ? colors.redAccent[700] : colors.blueAccent[800],
              color: colors.grey[100],
            },
          }}
        >
          {isConnected ? "Disconnect Draft" : "Connect Live Draft"}
        </Button>
      </Box>

      {/* Mobile Controls entry (XS/SM) */}
      <Tooltip title="Filters">
        <IconButton
          onClick={() => setFiltersOpen(true)}
          sx={{ 
            display: { xs: 'inline-flex', sm: 'inline-flex', md: 'none' },
            minWidth: '48px',
            minHeight: '48px',
            padding: '12px',
          }}
          aria-label="Open filters"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 5h18v2H3V5zm4 6h10v2H7v-2zm-2 6h14v2H5v-2z" />
          </svg>
        </IconButton>
      </Tooltip>

      {/* Filters Bottom Sheet for mobile */}
      <Drawer 
        anchor="bottom"
        open={filtersOpen} 
        onClose={() => setFiltersOpen(false)}
        PaperProps={{ 
          sx: { 
            backgroundColor: colors.primary[500],
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxHeight: '60vh',
          } 
        }}
        style={{ 
          zIndex: 1300 // Above the permanent drawer
        }}
      >
        {/* Drag handle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          pt: 1, 
          pb: 0 
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '4px', 
            backgroundColor: colors.grey[400], 
            borderRadius: '2px' 
          }} />
        </Box>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.primary[600]}`,
        }}>
          <Typography variant="h6" sx={{ color: colors.grey[100] }}>Filters</Typography>
          <IconButton
            onClick={() => setFiltersOpen(false)}
            sx={{ 
              color: colors.grey[100],
              minWidth: '48px',
              minHeight: '48px',
              padding: '12px',
            }}
            aria-label="Close filters"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </IconButton>
        </Box>
        <Box p={2} display="flex" flexDirection="column" gap={2}>
          {/* Keep Settings chips visible */}
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography variant="body2" sx={{ color: colors.grey[100], fontSize: "12px", fontWeight: "500" }}>Settings:</Typography>
            <Chip label={scoringType === "standard" ? "Standard" : scoringType === "ppr" ? "PPR" : "Half PPR"} size="small" sx={{ backgroundColor: colors.greenAccent[600], color: colors.grey[100], fontSize: "11px", height: "20px", fontWeight: "bold" }} />
            <Chip label={(
              { sleeper: 'Sleeper', yahoo: 'Yahoo', espn: 'ESPN', cbs: 'CBS', nfl: 'NFL' }[platform] || platform
            )} size="small" sx={{ backgroundColor: colors.blueAccent[600], color: colors.grey[100], fontSize: "11px", height: "20px", fontWeight: "bold" }} />
            {draftId && (
              <Chip label={`Draft: ${draftId.slice(0, 8)}...`} size="small" sx={{ backgroundColor: colors.redAccent[600], color: colors.grey[100], fontSize: "11px", height: "20px", fontWeight: "bold" }} />
            )}
            {isSuperflex && (
              <Chip label="2QB" size="small" sx={{ backgroundColor: colors.yellowAccent ? colors.yellowAccent[600] : colors.blueAccent[700], color: colors.grey[900], fontSize: "11px", height: "20px", fontWeight: "bold" }} />
            )}
          </Box>
          <Divider sx={{ borderColor: colors.primary[600] }} />

          {/* Positions */}
          <Box sx={{ border: `1px solid ${colors.primary[600]}`, borderRadius: '6px', p: 2, backgroundColor: colors.primary[400] }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {Object.entries(positionFilters).map(([position, checked]) => (
                <Chip
                  key={position}
                  label={position}
                  clickable
                  onClick={() => handlePositionFilterChange(position)}
                  variant={checked ? 'filled' : 'outlined'}
                  sx={{
                    height: 36,
                    minWidth: 48,
                    fontSize: '12px',
                    fontWeight: checked ? 600 : 500,
                    backgroundColor: checked ? (positionColors[position] || colors.blueAccent[600]) : 'transparent',
                    color: checked ? colors.grey[900] : colors.grey[100],
                    borderColor: colors.primary[600],
                    '&:hover': {
                      backgroundColor: checked ? (positionColors[position] || colors.blueAccent[500]) : colors.primary[600],
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Sorting */}
          <Box sx={{ border: `1px solid ${colors.primary[600]}`, borderRadius: '6px', p: 2, backgroundColor: colors.primary[400] }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title={!isConnected ? "Connect to a live draft to enable live mode" : ""}>
                <FormControlLabel
                  control={<Checkbox checked={isConnected && isLiveMode} onChange={handleLiveModeChange} disabled={!isConnected} size="small" sx={{ color: colors.grey[400], p: '2px', "&.Mui-checked": { color: colors.greenAccent[500] }, "&.Mui-disabled": { color: colors.grey[600] }, "& .MuiSvgIcon-root": { fontSize: '1.2rem' } }} />}
                  label={<Typography sx={{ fontSize: '11px', color: !isConnected ? colors.grey[500] : isLiveMode ? colors.greenAccent[400] : colors.grey[100], fontWeight: isLiveMode ? 'bold' : 'normal' }}>Live</Typography>}
                  sx={{ m: 0, "&.Mui-disabled": { cursor: 'not-allowed' } }}
                />
              </Tooltip>
              <RadioGroup row value={sortOption} onChange={(e) => handleSortChange(e)}>
                <FormControlLabel
                  value="adp"
                  control={<Radio size="small" sx={{ color: colors.grey[300], "&.Mui-checked": { color: colors.greenAccent[500] } }} />}
                  label={<Typography sx={{ fontSize: '11px', fontWeight: sortOption === 'adp' ? 700 : 500, color: sortOption === 'adp' ? colors.greenAccent[400] : colors.grey[100] }}>ADP</Typography>}
                  sx={{ m: 0, mr: 1 }}
                />
                <FormControlLabel
                  value="ecr"
                  control={<Radio size="small" sx={{ color: colors.grey[300], "&.Mui-checked": { color: colors.blueAccent[500] } }} />}
                  label={<Typography sx={{ fontSize: '11px', fontWeight: sortOption === 'ecr' ? 700 : 500, color: sortOption === 'ecr' ? colors.blueAccent[400] : colors.grey[100] }}>ECR</Typography>}
                  sx={{ m: 0 }}
                />
              </RadioGroup>
            </Box>
          </Box>

          {/* Connect Draft Button for Mobile */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant={isConnected ? "outlined" : "contained"}
              onClick={() => {
                connectLiveDraft();
                setFiltersOpen(false);
              }}
              sx={{
                backgroundColor: isConnected ? "transparent" : colors.blueAccent[700],
                borderColor: isConnected ? colors.redAccent[500] : "transparent",
                color: isConnected ? colors.redAccent[500] : colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "12px 24px",
                minHeight: "48px",
                "&:hover": {
                  backgroundColor: isConnected ? colors.redAccent[700] : colors.blueAccent[800],
                  color: colors.grey[100],
                },
              }}
            >
              {isConnected ? "Disconnect Draft" : "Connect Live Draft"}
            </Button>
          </Box>

          {/* Reset Draftboard (mobile) - inline panel with checkboxes */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ p: 1.5, border: `1px solid ${colors.primary[600]}`, borderRadius: '8px', backgroundColor: colors.primary[500] }}>
                <Typography variant="subtitle2" sx={{ color: colors.grey[100], fontWeight: 800, mb: 1 }}>Reset Draftboard</Typography>
                <FormControlLabel
                  control={<Checkbox size="small" checked={resetDrafted} onChange={(e) => setResetDrafted(e.target.checked)} sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[500] } }} />}
                  label={<Typography sx={{ color: colors.grey[100], fontSize: 14 }}>Reset drafted picks</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={resetKeepers} onChange={(e) => setResetKeepers(e.target.checked)} sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.redAccent[400] } }} />}
                  label={<Typography sx={{ color: colors.grey[100], fontSize: 14 }}>Reset keepers / assigned positions</Typography>}
                />
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    resetDraftboard({ resetDrafted, resetKeepers });
                    setFiltersOpen(false);
                  }}
                  sx={{
                    borderColor: colors.redAccent[500],
                    color: colors.redAccent[400],
                    fontWeight: 800,
                    mt: 1,
                    width: '100%'
                  }}
                >
                  Confirm Reset
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DraftboardControls;